import { useState, KeyboardEvent } from "react";
import { X, Plus, Tag } from "lucide-react";
import type { TheirStackTechManager } from "../api/TheirStackJobsApi";
import { useTheirStackJobs } from "../modal/TheirStackJobsContext";

interface Props {
  techManager: TheirStackTechManager;
  onClose: () => void;
}

export default function TechTagsModal({ techManager, onClose }: Props) {
  const { handleUpdateTechTags } = useTheirStackJobs();
  const [tags, setTags] = useState<string[]>(techManager.tech_tags);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addTag() {
    const trimmed = input.trim().toLowerCase();
    if (!trimmed || tags.includes(trimmed)) {
      setInput("");
      return;
    }
    setTags((prev) => [...prev, trimmed]);
    setInput("");
  }

  function removeTag(tag: string) {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1));
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await handleUpdateTechTags(techManager.employee_code, tags);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-surface border border-border rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-foreground font-inter">Tech Tags</h2>
            <p className="text-xs text-muted-foreground font-inter mt-0.5">{techManager.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="text-xs text-muted-foreground font-inter mb-3">
            Tags are used to auto-assign jobs. A job's <code className="text-primary">stack_source</code> is matched against these tags (case-insensitive).
          </p>

          {/* Tag pills */}
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[32px]">
            {tags.map((tag) => (
              <span
                key={tag}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg bg-primary/15 text-primary border border-primary/30 font-inter"
              >
                <Tag className="w-2.5 h-2.5" />
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="text-primary hover:text-foreground transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {tags.length === 0 && (
              <span className="text-xs text-muted-foreground font-inter italic">No tags yet</span>
            )}
          </div>

          {/* Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a tag and press Enter…"
              className="flex-1 bg-surface-muted border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
            />
            <button
              onClick={addTag}
              className="flex items-center gap-1 px-3 py-2 bg-surface-muted border border-border hover:border-border text-foreground hover:text-foreground rounded-lg text-xs font-inter transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>

          {error && <p className="text-[11px] text-destructive font-inter mt-2">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors"
          >
            {saving ? "Saving…" : "Save Tags"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border text-muted-foreground hover:text-foreground text-sm font-inter rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
